import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Chip,
  CircularProgress, Divider, Alert,
} from '@mui/material';
import CloseIcon  from '@mui/icons-material/Close';
import SendIcon   from '@mui/icons-material/Send';
import CheckIcon  from '@mui/icons-material/Check';
import { APPLAB_ACCENT } from '../constants';
import { authFetch } from '../../../utils/authFetch';

function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

export default function AppLabMailDialog({ open, onClose, shareUrl, appName, description }) {
  const [recipientInput, setRecipientInput] = useState('');
  const [recipients,     setRecipients]     = useState([]);
  const [subject,        setSubject]        = useState('');
  const [body,           setBody]           = useState('');
  const [sending,        setSending]        = useState(false);
  const [sent,           setSent]           = useState(false);
  const [error,          setError]          = useState('');

  function handleOpen() {
    setSubject(appName ? `${appName} – ta en titt` : 'En app till dig');
    setBody(description ? `${description}\n\nÖppna appen här: ${shareUrl}` : `Öppna appen här: ${shareUrl}`);
    setRecipients([]);
    setRecipientInput('');
    setSent(false);
    setError('');
  }

  function addRecipient(raw) {
    const trimmed = raw.trim().replace(/,|;|\s/g, '');
    if (!isValidEmail(trimmed)) return;
    if (recipients.some(r => r.email === trimmed)) return;
    setRecipients(r => [...r, { name: trimmed, email: trimmed }]);
    setRecipientInput('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addRecipient(recipientInput);
    }
  }

  async function handleSend() {
    if (recipients.length === 0) return;
    setSending(true);
    setError('');
    try {
      const res = await authFetch('/.netlify/functions/pitch-mail', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          bodyText:     body,
          heroImageUrl: '',
          shareUrl,
          pitchTitle:   appName,
          recipients,
        }),
      });
      if (!res.ok) throw new Error('Mejl misslyckades');
      setSent(true);
    } catch (e) {
      setError(e.message || 'Kunde inte skicka mejl');
    } finally {
      setSending(false);
    }
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: APPLAB_ACCENT },
      '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
    },
    '& label': { color: '#888' },
    '& label.Mui-focused': { color: APPLAB_ACCENT },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
      PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <SendIcon sx={{ color: APPLAB_ACCENT, fontSize: 18 }} />
        <Typography fontWeight={700} sx={{ flex: 1 }}>Skicka via mejl</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#666', '&:hover': { color: 'white' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sent ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <CheckIcon sx={{ color: '#4caf50', fontSize: 48 }} />
            <Typography fontWeight={700}>Mejl skickat!</Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {recipients.length} mottagare
            </Typography>
          </Box>
        ) : (
          <>
            {/* Mottagare */}
            <Box>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.75 }}>
                Mottagare (tryck Enter eller komma för att lägga till)
              </Typography>
              <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 0.75, minHeight: 40,
                bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 1.5, p: 1, alignItems: 'center',
              }}>
                {recipients.map(r => (
                  <Chip
                    key={r.email} label={r.email} size="small"
                    onDelete={() => setRecipients(rs => rs.filter(x => x.email !== r.email))}
                    sx={{ bgcolor: 'rgba(0,137,123,0.2)', color: '#a7d7d3', fontSize: '0.75rem' }}
                  />
                ))}
                <input
                  value={recipientInput}
                  onChange={e => setRecipientInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => addRecipient(recipientInput)}
                  placeholder={recipients.length === 0 ? 'namn@foretag.se' : ''}
                  style={{
                    background: 'transparent', border: 'none', outline: 'none',
                    color: 'white', fontSize: '0.85rem', flex: 1, minWidth: 140,
                  }}
                />
              </Box>
            </Box>

            <TextField
              label="Ämne" size="small" fullWidth
              value={subject} onChange={e => setSubject(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              label="Meddelande" size="small" fullWidth multiline rows={4}
              value={body} onChange={e => setBody(e.target.value)}
              sx={fieldSx}
            />

            {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Stäng</Button>
        {!sent && (
          <Button
            variant="contained" onClick={handleSend}
            disabled={sending || recipients.length === 0}
            startIcon={sending ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SendIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00695C' } }}
          >
            {sending ? 'Skickar…' : `Skicka${recipients.length > 0 ? ` till ${recipients.length}` : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
